import type { Database } from '@/integrations/supabase/types';

// Query key factory for React Query
export const PROFILE_QUERY_KEYS = {
  all: ['profile'] as const,
  detail: (userId: string) => ['profile', userId] as const,
  avatar: (userId: string) => ['profile', 'avatar', userId] as const,
  preferences: (userId: string) => ['profile', 'preferences', userId] as const,
  activity: (userId: string) => ['profile-activity', userId] as const,
  snapshots: (userId: string) => ['profile-snapshots', userId] as const,
} as const;

// Extract profile type from Supabase generated types
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type ProfileInsert = Database['public']['Tables']['profiles']['Insert'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

// User Profile Interface - Complete profile data structure
export interface UserProfile {
  id: string;
  user_id: string;
  legal_first_name: string | null;
  legal_last_name: string | null;
  preferred_first_name?: string | null;
  role?: string | null;
  position_title?: string | null;
  join_date?: string | null;
  profile_image?: string | null;
  business_email: string | null;
  personal_email?: string | null;
  business_phone?: string | null;
  personal_phone?: string | null;
  home_address?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  } | null;
  work_address?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  } | null;
  connected_accounts?: {
    google?: { connected: boolean; email?: string };
    linkedin?: { connected: boolean; profileUrl?: string };
    quickbooks?: { connected: boolean; companyId?: string };
    xero?: { connected: boolean; tenantId?: string };
  } | null;
  early_access?: boolean | null;
  bio?: string | null;
  company_name?: string | null;
  industry?: string | null;
  company_size?: string | null;
  business_structure?: string | null;
  goals?: string[] | null;
  has_specific_business?: string | null;
  successor_relationship?: string | null;
  advisor_relationship?: string | null;
  client_count?: string | null;
  years_in_business?: number | null;
  created_at: string;
  updated_at: string;
}

// Profile Update Payload - All fields optional for partial updates
export interface ProfileUpdatePayload {
  legal_first_name?: string;
  legal_last_name?: string;
  preferred_first_name?: string;
  role?: string;
  position_title?: string;
  join_date?: string;
  profile_image?: string;
  business_email?: string;
  personal_email?: string;
  business_phone?: string;
  personal_phone?: string;
  home_address?: UserProfile['home_address'];
  work_address?: UserProfile['work_address'];
  connected_accounts?: UserProfile['connected_accounts'];
  early_access?: boolean;
  bio?: string;
  company_name?: string;
  industry?: string;
  company_size?: string;
  business_structure?: string;
  goals?: string[];
  has_specific_business?: string;
  successor_relationship?: string;
  advisor_relationship?: string;
  client_count?: string;
  years_in_business?: number;
}

// Image Upload Progress Tracking
export interface ImageUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// Avatar Upload Result
export interface AvatarUploadResult {
  url: string;
  size: number;
  format: string;
  uploadedAt: string;
}

// Constants for image handling
export const IMAGE_CONSTRAINTS = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] as const,
  MAX_DIMENSIONS: {
    width: 2048,
    height: 2048,
  },
  COMPRESSION: {
    quality: 0.85,
    maxWidth: 1024,
    maxHeight: 1024,
  },
} as const;

// Default profile preferences
export const PROFILE_DEFAULTS = {
  early_access: false,
  connected_accounts: {},
  goals: [],
} as const;

// Error messages for profile operations
export const PROFILE_ERRORS = {
  NOT_AUTHENTICATED: 'User not authenticated',
  FILE_TOO_LARGE: `File size exceeds ${IMAGE_CONSTRAINTS.MAX_SIZE / 1024 / 1024}MB limit`,
  INVALID_FILE_TYPE: `File type not allowed. Supported types: ${IMAGE_CONSTRAINTS.ALLOWED_TYPES.join(', ')}`,
  UPLOAD_FAILED: 'Failed to upload image',
  UPDATE_FAILED: 'Failed to update profile',
  DELETE_FAILED: 'Failed to delete profile',
  FETCH_FAILED: 'Failed to fetch profile',
  PERMISSION_DENIED: 'You do not have permission to perform this action',
  COMPRESSION_FAILED: 'Failed to compress image',
} as const;
